import Vue from 'vue';
import { Module, Store, ActionContext as VuexActionContext, CommitOptions, DispatchOptions } from 'vuex';
import { PromisifyIfNot } from '@/scripts/models/utility-types';

// Utility Types
type FunctionDictionary<T> = {
  [P in keyof T]: (...args: any) => any
}
type PickParameter<F extends (...args: any[]) => any, Index extends keyof Parameters<F> = 0> = Parameters<F>[Index];
/**
 * This make params help make the first params as optional if the provided P is undefined.
 */
type MakeParams<P, O> = P extends undefined ? [undefined?, O?] : [P, O?]
type PayloadWithOptions<Payload, Options> = Payload & { options: Options };
type PayloadWithType<TypeName, Payload> = Payload & { type: TypeName };

type GetterTree<State, Getters, RootState, RootGetters> = {
  [P in keyof Getters]: (
    this: Store<State>,
    state: State,
    getters: Omit<Getters, P>,
    rootState: RootState,
    rootGetters: RootGetters
  ) => Getters[P]
}

// Store related types
interface MutationModuleCommittor<Mutations extends FunctionDictionary<Mutations>> {
  <P extends keyof Mutations>(commitName: P, ...params: MakeParams<PickParameter<Mutations[P]>, CommitOptions>): void;
  <P extends keyof Mutations>(options: PayloadWithType<P, PayloadWithOptions<PickParameter<Mutations[P]>, CommitOptions>>): void;
}

interface MutationMappedCommittor<Mutations extends FunctionDictionary<Mutations>> {
  <P extends keyof Mutations>(...params: MakeParams<PickParameter<Mutations[P]>, CommitOptions>): void;
  <P extends keyof Mutations>(options: PayloadWithOptions<PickParameter<Mutations[P]>, CommitOptions>): void;
}

type MutationTree<State, Mutations extends FunctionDictionary<Mutations>> = {
  [P in keyof Mutations]: (
    this: Store<State>,
    state: State,
    payload: PickParameter<Mutations[P]>,
  ) => ReturnType<Mutations[P]>
}

interface ActionModuleDispatcher<Actions extends FunctionDictionary<Actions>> {
  <P extends keyof Actions>(actionName: P, ...params: MakeParams<PickParameter<Actions[P]>, DispatchOptions>): PromisifyIfNot<ReturnType<Actions[P]>>;
  <P extends keyof Actions>(options: PayloadWithType<P, PayloadWithOptions<PickParameter<Actions[P]>, DispatchOptions>>): PromisifyIfNot<ReturnType<Actions[P]>>;
}

interface ActionMappedDispatcher<Actions extends FunctionDictionary<Actions>> {
  <P extends keyof Actions>(...params: MakeParams<PickParameter<Actions[P]>, DispatchOptions>): PromisifyIfNot<ReturnType<Actions[P]>>;
  <P extends keyof Actions>(options: PayloadWithOptions<PickParameter<Actions[P]>, DispatchOptions>): PromisifyIfNot<ReturnType<Actions[P]>>;
};

export type ActionContext<State, Getters, Mutations extends FunctionDictionary<Mutations>, Actions extends FunctionDictionary<Actions>, RootState, RootGetters> = (
  Omit<VuexActionContext<State, RootState>, 'commit' | 'dispatch' | 'getters' | 'rootGetters'> &
  {
    commit: MutationModuleCommittor<Mutations>;
    dispatch: ActionModuleDispatcher<Actions>;
    getters: Getters;
    rootGetters: RootGetters;
  }
);

type ActionTree<State, Getters, Mutations extends FunctionDictionary<Mutations>, Actions extends FunctionDictionary<Actions>, RootState, RootGetters> = {
  [P in keyof Actions]: (
    this: Store<State>,
    context: ActionContext<State, Getters, Mutations, Actions, RootState, RootGetters>,
    payload: PickParameter<Actions[P]>,
  ) => ReturnType<Actions[P]>;
}

// Exported declarations
export type StoreModule<State, Getters, Mutations extends FunctionDictionary<Mutations>, Actions extends FunctionDictionary<Actions>, RootState, RootGetters> = (
  Omit<Module<State, RootState>, 'getters' | 'mutations' | 'actions'> &
  {
    getters?: GetterTree<State, Getters, RootState, RootGetters>;
    mutations?: MutationTree<State, Mutations>;
    actions?: ActionTree<State, Getters, Mutations, Actions, RootState, RootGetters>;
  }
)

// Exported implementations
export const createStoreModule = <State, Getters, Mutations extends FunctionDictionary<Mutations>, Actions extends FunctionDictionary<Actions>>(
  x: StoreModule<State, Getters, Mutations, Actions, any, any>,
): StoreModule<State, Getters, Mutations, Actions, any, any> => x;

// Exported map providers

/**
 * Warn developer that store must be registered before the provider function can be used.
 *
 * This code will only be included in non-production environment
 * @param vm
 * @param path
 * @returns
 */
const assertModuleRegistration = (vm: InstanceType<typeof Vue>, path: string): void => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  if (process.env.NODE_ENV === 'production') return;

  if (vm.$store.hasModule(path)) return;

  throw new Error('Store provider is used before module is registered');
};

export const provideMapState = <State>(path: string) => () =>
  function mapState(this: InstanceType<typeof Vue>): State {
    assertModuleRegistration(this, path);
    return this.$store.state[path];
  }
;

export const provideMapGetter = <Getters>(path: string) => <P extends keyof Getters>(name: P) =>
  function mapGetter(this: InstanceType<typeof Vue>): Getters[P] {
    assertModuleRegistration(this, path);
    return this.$store.getters[`${path}/${name}`];
  }
;

export const provideMapMutation = <Mutations extends FunctionDictionary<Mutations>>(path: string) =>
  <P extends keyof Mutations>(name: P): MutationMappedCommittor<Pick<Mutations, P>> =>
    function mapMutation(this: InstanceType<typeof Vue>, ...args: any[]): void {
      assertModuleRegistration(this, path);
      return this.$store.commit(`${path}/${name}`, ...args);
    }
;

export const provideMapAction = <Actions extends FunctionDictionary<Actions>>(path: string) =>
  <P extends keyof Actions>(name: P): ActionMappedDispatcher<Pick<Actions, P>> =>
    function mapAction(this: InstanceType<typeof Vue>, ...args: any[]): any {
      assertModuleRegistration(this, path);
      return this.$store.dispatch(`${path}/${name}`, ...args) as any;
    }
;
